// 職責：純前端的介面狀態 —— 側欄形態、主題偏好、以及全域 toast 佇列

/*
 * 為什麼這些不放 TanStack Query：doc 06 §1 的分層 —— 伺服器資料歸 Query，
 * 「側欄收合／主題／選取」這類無來源、無失效、無重取語義的狀態歸 Pinia。
 *
 * 為什麼 toast 是佇列而不是單一物件（doc 09 §8.2）：
 *   ops-console 只有一個 toast 欄位，後一則會覆蓋前一則。批次操作（例如逐筆儲存）
 *   一次可產生多則結果，被覆蓋掉的那幾則正是使用者最需要看到的失敗訊息。
 */

import { defineStore } from 'pinia'

/* ────────────────────────────── 主題 ────────────────────────────── */

/**
 * `'system'` 表示不指定 `data-theme`，交由 tokens.css 的
 * `@media (prefers-color-scheme: dark)` 決定；`'light'` / `'dark'` 則寫入屬性，
 * 因此能**雙向覆蓋**系統偏好（深色系統選淺色亦然，doc 06 §4.2）。
 */
export type ThemePreference = 'light' | 'dark' | 'system'

const KEY_THEME = 'shopmgr-theme'
const KEY_SIDEBAR = 'shopmgr-sidebar-collapsed'

function readStorage(key: string): string | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key: string, value: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, value)
  } catch {
    // 配額用盡或隱私模式：偏好存不下來不影響本次操作
  }
}

function restoreTheme(): ThemePreference {
  const raw = readStorage(KEY_THEME)
  return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system'
}

/** 唯一寫 `<html data-theme>` 的地方，避免主題來源分裂成 CSS 與 JS 兩套 */
function applyTheme(theme: ThemePreference): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (theme === 'system') {
    root.removeAttribute('data-theme')
  } else {
    root.setAttribute('data-theme', theme)
  }
}

/* ────────────────────────────── Toast ────────────────────────────── */

/** 對應 doc 05 §3.1 的呈現分級；`error` 不自動消失，因為它常帶 requestId 需要抄下 */
export type ToastKind = 'success' | 'info' | 'warning' | 'error'

export interface Toast {
  id: number
  kind: ToastKind
  message: string
  /** 錯誤代碼／requestId，供使用者回報時對應後端 log（doc 05 §3.2）*/
  detail?: string
}

export interface ToastOptions {
  detail?: string
  /** 毫秒；0 表示不自動消失。未指定時依 kind 取預設 */
  durationMs?: number
}

/** 同時最多幾則 —— 超過就丟掉最舊的，避免批次失敗把整個畫面蓋住 */
const MAX_TOASTS = 4

const DEFAULT_DURATION_MS: Record<ToastKind, number> = {
  success: 3_000,
  info: 4_000,
  warning: 6_000,
  // 0 = 需使用者自行關閉：錯誤訊息含 requestId，自動消失會讓人抄不到
  error: 0,
}

/*
 * 計時器 handle 放模組層級而非 state：它們是不可序列化的宿主物件，
 * 進了 reactive state 只會讓 devtools 快照與潛在的持久化外掛出問題。
 */
const timers = new Map<number, ReturnType<typeof setTimeout>>()
let nextToastId = 1

function clearTimer(id: number): void {
  const handle = timers.get(id)
  if (handle !== undefined) {
    clearTimeout(handle)
    timers.delete(id)
  }
}

/* ────────────────────────────── Store ────────────────────────────── */

interface UiState {
  /** 桌面／中寬螢幕：側欄收合為圖示列（doc 06 §4.4 的 860–1280px）*/
  sidebarCollapsed: boolean
  /** <860px：側欄為覆蓋層抽屜，與上面的收合是兩種不同形態，不可共用同一個旗標 */
  mobileSidebarOpen: boolean
  theme: ThemePreference
  toasts: Toast[]
}

export const useUiStore = defineStore('ui', {
  state: (): UiState => ({
    sidebarCollapsed: readStorage(KEY_SIDEBAR) === 'true',
    mobileSidebarOpen: false,
    theme: restoreTheme(),
    toasts: [],
  }),

  actions: {
    /** 在 app 掛載時呼叫一次：把還原出來的偏好實際套到 DOM 上 */
    initTheme(): void {
      applyTheme(this.theme)
    },

    setTheme(theme: ThemePreference): void {
      this.theme = theme
      applyTheme(theme)
      writeStorage(KEY_THEME, theme)
    },

    /** 切換鈕的語義：只在明暗之間來回，不會把使用者丟回 `system` */
    toggleTheme(): void {
      this.setTheme(this.theme === 'dark' ? 'light' : 'dark')
    },

    setSidebarCollapsed(collapsed: boolean): void {
      this.sidebarCollapsed = collapsed
      writeStorage(KEY_SIDEBAR, String(collapsed))
    },

    toggleSidebar(): void {
      this.setSidebarCollapsed(!this.sidebarCollapsed)
    },

    setMobileSidebarOpen(open: boolean): void {
      this.mobileSidebarOpen = open
    },

    /**
     * 推一則 toast。
     * ⚠️ `message` 是給使用者看的繁體中文文案，**不得**放 token、密碼或原始例外堆疊。
     * @returns toast id，供呼叫端在流程結束時提前關閉（例如「儲存中…」換成「已儲存」）
     */
    toast(kind: ToastKind, message: string, options: ToastOptions = {}): number {
      const id = nextToastId++
      const item: Toast = { id, kind, message }
      if (options.detail !== undefined) item.detail = options.detail

      this.toasts.push(item)

      // 溢位時丟最舊的：最新的訊息通常對應使用者剛做的動作，優先保留
      while (this.toasts.length > MAX_TOASTS) {
        const dropped = this.toasts.shift()
        if (dropped !== undefined) clearTimer(dropped.id)
      }

      const duration = options.durationMs ?? DEFAULT_DURATION_MS[kind]
      if (duration > 0) {
        timers.set(
          id,
          setTimeout(() => {
            this.dismissToast(id)
          }, duration),
        )
      }
      return id
    },

    dismissToast(id: number): void {
      clearTimer(id)
      const index = this.toasts.findIndex((t) => t.id === id)
      if (index >= 0) this.toasts.splice(index, 1)
    },

    /** 路由切換等場景用：上一頁的提示留到下一頁只會造成困惑 */
    clearToasts(): void {
      for (const item of this.toasts) clearTimer(item.id)
      this.toasts = []
    },
  },
})
